import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserData } from '../../../services/auth';
import { getPublishedTrips } from '../../../services/firestore/trips';
import QuestionnaireModal from '../../../components/QuestionnaireModal';
import TripCard from '../../../components/TripCard';

const ClientHome = () => {
  const { user } = useAuth();
  const [userName, setUserName] = useState('User');
  const [trips, setTrips] = useState([]);
  const [groupTrips, setGroupTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [hasCompletedQuestionnaire, setHasCompletedQuestionnaire] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        // Load user data
        const userData = await getUserData(user.uid);
        if (userData) {
          const firstName = userData.displayName?.split(' ')[0] || user.displayName?.split(' ')[0] || 'User';
          setUserName(firstName);
          
          // Check if user has completed questionnaire
          setHasCompletedQuestionnaire(!!userData.questionnaireCompleted);
          
          // Show questionnaire if not completed
          if (!userData.questionnaireCompleted) {
            setShowQuestionnaire(true);
          }
        }

        // Load trips
        const allTrips = await getPublishedTrips();
        
        // Filter for recommended trips (can be personalized based on questionnaire later)
        const recommendedTrips = allTrips.slice(0, 3);
        setTrips(recommendedTrips);
        
        // Filter for group trips (trips with capacity > 4)
        const groups = allTrips.filter(trip => trip.capacity > 4).slice(0, 2);
        setGroupTrips(groups);
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const handleQuestionnaireComplete = () => {
    setShowQuestionnaire(false);
    setHasCompletedQuestionnaire(true);
    // Reload trips with personalized recommendations
    // This can be enhanced to filter based on questionnaire responses
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
      {/* Hero Section with Greeting */}
      <div className="bg-gradient-to-b from-emerald-50/40 to-white w-full px-4 sm:px-6 lg:px-8 pt-8 pb-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-2">
            Hello, {userName}.
          </h1>
          <p className="text-base sm:text-lg text-gray-600 mb-6">
            Ready to restore?
          </p>
          
          {/* Mood Buttons */}
          <div className="grid grid-cols-4 gap-3 max-w-md">
            {['😊', '😌', '😫', '🤗'].map((emoji, index) => (
              <button
                key={index}
                className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all hover:scale-105 text-2xl"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recommended for You Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Recommended for You
          </h2>
          <Link
            to="/available-listings"
            className="text-sm sm:text-base text-emerald-700 hover:text-emerald-800 font-medium"
          >
            See all
          </Link>
        </div>

        {/* Retreat Cards Carousel */}
        {trips.length > 0 ? (
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {trips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                variant="default"
                showPhotoCount={true}
                showUsers={true}
                showCalendar={false}
                showClock={false}
                className="flex-shrink-0 w-80"
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50">
            <p className="text-gray-600">No recommendations available at the moment.</p>
            <Link
              to="/available-listings"
              className="inline-block mt-4 text-emerald-700 hover:text-emerald-800 font-medium"
            >
              Browse all trips →
            </Link>
          </div>
        )}
      </div>

      {/* Group Getaways Section */}
      {groupTrips.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Group Getaways
            </h2>
            <Link
              to="/available-listings"
              className="text-sm sm:text-base text-emerald-700 hover:text-emerald-800 font-medium"
            >
              See all
            </Link>
          </div>

          {/* Group Cards Carousel */}
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {groupTrips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                variant="default"
                showPhotoCount={true}
                showUsers={true}
                showCalendar={false}
                showClock={false}
                className="flex-shrink-0 w-80"
              />
            ))}
          </div>
        </div>
      )}

      {/* Questionnaire Modal */}
      {showQuestionnaire && (
        <QuestionnaireModal
          isOpen={showQuestionnaire}
          onClose={() => setShowQuestionnaire(false)}
          onComplete={handleQuestionnaireComplete}
        />
      )}
    </div>
  );
};

export default ClientHome;
